import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";

import {
  ThemeProvider,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Box,
  Breadcrumbs,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { createTheme } from "@mui/material/styles";
import EventIcon from "@mui/icons-material/Event";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getJobOrders } from "../API/jobOrderAPI";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

function JobOrderList() {
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Format: dd-MMM-yyyy hh:mm:ss a
    const formattedDate = format(date, "dd-MMM-yyyy hh:mm:ss a");

    return formattedDate;
  };

  useEffect(() => {
    const fetchJobOrders = async () => {
      try {
        const jobOrdersData = await getJobOrders();
        if (Array.isArray(jobOrdersData)) {
          // Combine date and time fields
          const formattedJobOrders = jobOrdersData.map((jobOrder) => {
            const combinedStartDateTime = new Date(
              `${jobOrder.estimated_start_date}T${jobOrder.estimated_start_time}`
            );
            const combinedEndDateTime = new Date(
              `${jobOrder.estimated_end_date}T${jobOrder.estimated_end_time}`
            );
            return {
              ...jobOrder,
              combinedStartDateTime,
              combinedEndDateTime,
              formattedCombinedStartDateTime: formatDate(combinedStartDateTime),
              formattedCombinedEndDateTime: formatDate(combinedEndDateTime),
            };
          });

          // Sort by combinedStartDateTime, from earliest to latest
          const sortedJobOrders = formattedJobOrders.sort(
            (a, b) => a.combinedStartDateTime - b.combinedStartDateTime
          );
          setJobOrders(sortedJobOrders);
        } else {
          setJobOrders([]);
        }

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchJobOrders();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredJobOrders = jobOrders.filter((jobOrder) =>
    [
      jobOrder.client_name,
      jobOrder.job_site_address,
      jobOrder.type,
      jobOrder.treatment_description,
      jobOrder.treatment_type,
      jobOrder.pest_problem,
      jobOrder.team,
      jobOrder.technicians,
      jobOrder.status,
    ].some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ padding: "40px 60px" }}>
        <Card
          variant="outlined"
          style={{
            marginTop: "40px",
            marginBottom: "40px",
            borderRadius: 0,
          }}
        >
          <CardHeader
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center">
                  <EventIcon
                    sx={{
                      fontSize: "40px",
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="h6">Operations</Typography>
                </Box>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<NavigateNextIcon fontSize="small" />}
                  sx={{ color: "white" }}
                >
                  <Typography sx={{ color: "#ffffff99" }}>
                    Operations
                  </Typography>
                  <Typography
                    component={Link}
                    to="/list-job-orders"
                    sx={{ color: "#ffffff", textDecoration: "none" }}
                  >
                    Job Orders
                  </Typography>
                </Breadcrumbs>
              </Box>
            }
            sx={{
              backgroundColor: "#1e5d45",
              color: "white",
              padding: "16px",
            }}
          />

          <CardContent sx={{ padding: "20px" }}>
            <Card variant="outlined" sx={{ padding: "20px" }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Job Orders
                  </Typography>
                }
                action={
                  <Button
                    component={Link}
                    to="/create-job-order"
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                  >
                    Create Job Order
                  </Button>
                }
                sx={{ marginBottom: 2 }}
              />
              <TextField
                variant="outlined"
                placeholder="Search by client name, address, treatment, type, problem, team, or status..."
                value={searchQuery}
                onChange={handleSearchChange}
                fullWidth
                sx={{ marginBottom: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 2,
                    paddingBottom: "20px",
                  }}
                >
                  <CircularProgress />
                  <Typography variant="body1" sx={{ marginLeft: 2 }}>
                    Loading job orders...
                  </Typography>
                </Box>
              ) : error ? (
                <Typography variant="body1" color="error" sx={{ padding: 2 }}>
                  Error fetching job orders: {error}
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Client
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Job Site Address
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Treatment Description
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Treatment Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Pest Problem
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Start Schedule
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            End Schedule
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Team
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Technicians
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              backgroundColor: "#f5f5f5",
                              textAlign: "center",
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredJobOrders.length > 0 ? (
                          filteredJobOrders
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((jobOrder, index) => (
                              <TableRow key={index}>
                                <TableCell>{jobOrder.client_name}</TableCell>
                                <TableCell>
                                  {jobOrder.job_site_address}
                                </TableCell>
                                <TableCell>{jobOrder.type}</TableCell>
                                <TableCell>
                                  {jobOrder.treatment_description}
                                </TableCell>
                                <TableCell>{jobOrder.treatment_type}</TableCell>
                                <TableCell>{jobOrder.pest_problem}</TableCell>
                                <TableCell>
                                  {jobOrder.formattedCombinedStartDateTime}
                                </TableCell>
                                <TableCell>
                                  {jobOrder.formattedCombinedEndDateTime}
                                </TableCell>
                                <TableCell>{jobOrder.team}</TableCell>
                                <TableCell>
                                  {jobOrder.technicians}
                                </TableCell>
                                <TableCell>{jobOrder.status}</TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Tooltip title="View">
                                      <IconButton
                                        component={Link}
                                        to={`/view-job-order/${jobOrder.id}`}
                                      >
                                        <VisibilityIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#6C757D",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Update">
                                      <IconButton
                                        component={Link}
                                        to={`/edit-job-order/${jobOrder.id}`}
                                      >
                                        <EditIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#007BFF",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        component={Link}
                                        to={`/delete-job-order/${jobOrder.id}`}
                                      >
                                        <DeleteIcon
                                          sx={{
                                            fontSize: "1.2rem",
                                            color: "#DC3545",
                                          }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={12} align="center">
                              No job orders found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={filteredJobOrders.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableContainer>
                </>
              )}
            </Card>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}

export default JobOrderList;
